/**
 * Client-side OT engine implementing the Jupiter protocol.
 *
 * State machine:
 *   SYNCHRONIZED ─── local edit ──→ AWAITING_CONFIRM
 *        ↑                              │
 *        │                         server ack
 *        │                              │
 *        └────── (no buffer) ───────────┘
 *                                       │
 *                                  (has buffer)
 *                                       │
 *                                       ▼
 *                             AWAITING_WITH_BUFFER
 *                               │            │
 *                         server ack    local edit
 *                               │            │
 *                               ▼            ▼
 *                       AWAITING_CONFIRM  (compose into buffer)
 *
 * The engine talks to Firestore via callbacks — it doesn't import Firebase directly.
 */

import {
  OTComponents,
  OTClientState,
  OTEngineEvent,
  OTOperation,
} from '../types/ot';
import { transform, compose, applyOp, compact } from './otTransform';

export interface OTEngineCallbacks {
  /** Send a pending operation to Firestore (write to pendingOps subcollection) */
  sendOp(op: OTComponents, baseVersion: number): void;
  /** Notify listeners of engine events */
  onEvent(event: OTEngineEvent): void;
}

export class OTEngine {
  private state: OTClientState;
  private callbacks: OTEngineCallbacks;
  private destroyed = false;

  constructor(
    initialDocument: string,
    initialVersion: number,
    callbacks: OTEngineCallbacks
  ) {
    this.state = {
      type: 'synchronized',
      document: initialDocument,
      version: initialVersion,
      pending: null,
      buffer: null,
    };
    this.callbacks = callbacks;
  }

  /** Get current state (read-only snapshot) */
  getState(): Readonly<OTClientState> {
    return this.state;
  }

  /** Get current document content */
  getDocument(): string {
    return this.state.document;
  }

  /** Get current version */
  getVersion(): number {
    return this.state.version;
  }

  /** Whether the engine is fully synchronized with the server */
  isSynchronized(): boolean {
    return this.state.type === 'synchronized';
  }

  /**
   * Apply a local edit. The operation is applied optimistically to the document
   * and either sent immediately or buffered depending on state.
   */
  applyLocal(op: OTComponents): void {
    if (this.destroyed) return;

    const compacted = compact(op);
    if (compacted.length === 0) return; // no-op

    // Apply optimistically
    try {
      this.state.document = applyOp(this.state.document, compacted);
    } catch (e) {
      this.callbacks.onEvent({
        type: 'error',
        error: `Local apply failed: ${(e as Error).message}`,
      });
      return;
    }

    this.callbacks.onEvent({
      type: 'documentChanged',
      document: this.state.document,
      source: 'local',
    });

    switch (this.state.type) {
      case 'synchronized':
        // Send immediately, transition to awaitingConfirm
        this.state.pending = compacted;
        this.state.type = 'awaitingConfirm';
        this.callbacks.sendOp(compacted, this.state.version);
        this.emitStateChange();
        break;

      case 'awaitingConfirm':
        // Start buffering
        this.state.buffer = compacted;
        this.state.type = 'awaitingWithBuffer';
        this.emitStateChange();
        break;

      case 'awaitingWithBuffer':
        // Compose into existing buffer
        this.state.buffer = compose(this.state.buffer!, compacted);
        break;
    }
  }

  /**
   * Handle a server-acknowledged operation. This is called when we receive
   * our own operation back from the operations log (confirmed by the Cloud Function).
   */
  handleAck(serverVersion: number): void {
    if (this.destroyed) return;

    this.state.version = serverVersion;

    switch (this.state.type) {
      case 'awaitingConfirm':
        // Our pending op was confirmed
        this.state.pending = null;
        this.state.type = 'synchronized';
        this.emitStateChange();
        break;

      case 'awaitingWithBuffer':
        // Our pending op was confirmed, send buffer
        this.state.pending = this.state.buffer;
        this.state.buffer = null;
        this.state.type = 'awaitingConfirm';
        this.callbacks.sendOp(this.state.pending!, this.state.version);
        this.emitStateChange();
        break;

      case 'synchronized':
        // Unexpected ack while synchronized — ignore
        break;
    }
  }

  /**
   * Handle a remote operation from another user. The operation has already
   * been committed by the server and must be applied after transforming
   * against any pending/buffered local operations.
   */
  handleRemoteOp(serverOp: OTOperation): void {
    if (this.destroyed) return;

    // Skip if this is our own operation (handled by handleAck)
    // The caller should filter this, but guard just in case
    const op = serverOp.components;

    switch (this.state.type) {
      case 'synchronized': {
        // Apply directly
        try {
          this.state.document = applyOp(this.state.document, op);
        } catch (e) {
          this.callbacks.onEvent({
            type: 'error',
            error: `Remote apply failed: ${(e as Error).message}`,
          });
          return;
        }
        this.state.version = serverOp.version;
        this.callbacks.onEvent({
          type: 'documentChanged',
          document: this.state.document,
          source: 'remote',
        });
        this.callbacks.onEvent({
          type: 'remoteOp',
          op,
          userId: serverOp.userId,
        });
        break;
      }

      case 'awaitingConfirm': {
        // Transform server op against our pending op
        const [pendingPrime, opPrime] = transform(this.state.pending!, op);
        this.state.pending = pendingPrime;

        try {
          this.state.document = applyOp(this.state.document, opPrime);
        } catch (e) {
          this.callbacks.onEvent({
            type: 'error',
            error: `Remote apply (awaiting) failed: ${(e as Error).message}`,
          });
          return;
        }
        this.state.version = serverOp.version;
        this.callbacks.onEvent({
          type: 'documentChanged',
          document: this.state.document,
          source: 'remote',
        });
        this.callbacks.onEvent({
          type: 'remoteOp',
          op: opPrime,
          userId: serverOp.userId,
        });
        break;
      }

      case 'awaitingWithBuffer': {
        // Transform server op against pending, then against buffer
        const [pendingPrime, opPrime1] = transform(this.state.pending!, op);
        const [bufferPrime, opPrime2] = transform(this.state.buffer!, opPrime1);
        this.state.pending = pendingPrime;
        this.state.buffer = bufferPrime;

        try {
          this.state.document = applyOp(this.state.document, opPrime2);
        } catch (e) {
          this.callbacks.onEvent({
            type: 'error',
            error: `Remote apply (buffered) failed: ${(e as Error).message}`,
          });
          return;
        }
        this.state.version = serverOp.version;
        this.callbacks.onEvent({
          type: 'documentChanged',
          document: this.state.document,
          source: 'remote',
        });
        this.callbacks.onEvent({
          type: 'remoteOp',
          op: opPrime2,
          userId: serverOp.userId,
        });
        break;
      }
    }
  }

  /**
   * Reset the engine to a known state (e.g., after reconnection or fallback).
   */
  reset(document: string, version: number): void {
    this.state = {
      type: 'synchronized',
      document,
      version,
      pending: null,
      buffer: null,
    };
    this.emitStateChange();
    this.callbacks.onEvent({
      type: 'documentChanged',
      document,
      source: 'remote',
    });
  }

  /** Clean up */
  destroy(): void {
    this.destroyed = true;
  }

  private emitStateChange(): void {
    this.callbacks.onEvent({
      type: 'stateChanged',
      state: this.state.type,
    });
  }
}
